import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { sidebarDesign as ds } from './MainSidebar.design';

// px por milissegundo (~450px/s): varre o menu rapido sem passar do ponto.
const VELOCIDADE_AUTO = 0.45;

/**
 * Area rolavel do menu da sidebar com setas no topo e no rodape. As setas so
 * aparecem quando o conteudo realmente transborda (monitor baixo, muitos itens
 * de menu ou submenu aberto) e cada uma some ao chegar no fim daquele lado —
 * por isso a condicao e overflow real, e nao um breakpoint de resolucao.
 *
 * Vive em arquivo proprio (e nao dentro de MainSidebar.js) para poder ser
 * testado sem carregar o grafo inteiro da sidebar — o MainSidebar puxa a store
 * do redux, que instancia um Web Worker e nao sobe no jsdom.
 */
export const NavScrollArea = ({ className, children }) => {
    const { t } = useTranslation('chat');
    const scrollRef = useRef(null);
    const rafRef = useRef(null);
    const [podeSubir, setPodeSubir] = useState(false);
    const [podeDescer, setPodeDescer] = useState(false);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;

        const atualizar = () => {
            // Folga de 2px: zoom e telas HiDPI arredondam scrollHeight e a seta
            // de baixo ficaria acesa para sempre no fim da lista.
            const folga = 2;
            setPodeSubir(el.scrollTop > folga);
            setPodeDescer(el.scrollTop + el.clientHeight < el.scrollHeight - folga);
        };

        atualizar();
        el.addEventListener('scroll', atualizar, { passive: true });
        // Cobre janela redimensionada, sidebar expandindo/recolhendo e submenu
        // abrindo/fechando (que muda a altura do conteudo sem gerar scroll).
        const observer = new ResizeObserver(atualizar);
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);

        return () => {
            el.removeEventListener('scroll', atualizar);
            observer.disconnect();
        };
    }, []);

    // Desmontar no meio de uma rolagem automatica deixaria o rAF rodando.
    useEffect(() => pararAuto, []);

    const rolar = (direcao) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ top: direcao * Math.max(el.clientHeight * 0.7, 80), behavior: 'smooth' });
    };

    const pararAuto = () => {
        if (rafRef.current === null) return;
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
    };

    // Rolagem continua enquanto o mouse estiver sobre (ou perto de) o botao.
    // Passo por tempo decorrido, e nao por frame, para a velocidade nao mudar
    // em monitor de 120Hz.
    const iniciarAuto = (direcao) => {
        const el = scrollRef.current;
        if (!el) return;
        pararAuto();

        let anterior = null;
        const passo = (agora) => {
            // O primeiro frame so ancora o relogio: com decorrido = 0 nada anda,
            // e a checagem de ponta abaixo entenderia isso como fim do curso.
            if (anterior === null) {
                anterior = agora;
                rafRef.current = requestAnimationFrame(passo);
                return;
            }

            const decorrido = agora - anterior;
            anterior = agora;

            if (decorrido > 0) {
                const antes = el.scrollTop;
                el.scrollTop += direcao * VELOCIDADE_AUTO * decorrido;
                // Chegou na ponta: para sozinho, porque o botao some e o
                // mouseleave pode nunca acontecer.
                if (el.scrollTop === antes) {
                    pararAuto();
                    return;
                }
            }

            rafRef.current = requestAnimationFrame(passo);
        };
        rafRef.current = requestAnimationFrame(passo);
    };

    return (
        <div className={ds.navScroll.wrap}>
            {podeSubir && (
                <>
                    <div className={`${ds.navScroll.fade} ${ds.navScroll.fadeTop}`} />
                    <div
                        className={`${ds.navScroll.zone} ${ds.navScroll.zoneTop}`}
                        onMouseEnter={() => iniciarAuto(-1)}
                        onMouseLeave={pararAuto}
                    >
                        <button
                            type="button"
                            aria-label={t('sidebar.scrollUp', 'Rolar para cima')}
                            onClick={() => rolar(-1)}
                            className={ds.navScroll.button}
                        >
                            <Iconify icon="solar:alt-arrow-up-bold" width={15} />
                        </button>
                    </div>
                </>
            )}

            <div ref={scrollRef} className={className}>
                {children}
            </div>

            {podeDescer && (
                <>
                    <div className={`${ds.navScroll.fade} ${ds.navScroll.fadeBottom}`} />
                    <div
                        className={`${ds.navScroll.zone} ${ds.navScroll.zoneBottom}`}
                        onMouseEnter={() => iniciarAuto(1)}
                        onMouseLeave={pararAuto}
                    >
                        <button
                            type="button"
                            aria-label={t('sidebar.scrollDown', 'Rolar para baixo')}
                            onClick={() => rolar(1)}
                            className={ds.navScroll.button}
                        >
                            <Iconify icon="solar:alt-arrow-down-bold" width={15} />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default NavScrollArea;
